import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Loader2, ChevronRight } from "lucide-react";
import { useSEO, buildBreadcrumbSchema, buildCalculatorSchema, buildFAQSchema } from "../hooks/useSEO";
import { AdSlot } from "../components/AdSlot";
import { FilingStatusSelector } from "../components/FilingStatusSelector";
import { ResultExplainer, COMMON_GLOSSARY } from "../components/ResultExplainer";
import { taxAPI, formatCurrency, formatPercent } from "../lib/api";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

/**
 * StateIncomeTaxPage — /calculators/state-income-tax/:state
 *
 * Federal (OBBBA 2026) + state 2026 combined tax calculator. Each state
 * variant is a unique long-tail SEO landing page (51 total).
 */
const StateIncomeTaxPage = () => {
  const { state: stateSlug } = useParams();
  const [meta, setMeta] = useState(null);
  const [metaErr, setMetaErr] = useState(false);
  const [filing, setFiling] = useState("single");
  const [income, setIncome] = useState("85000");
  const [dependents, setDependents] = useState("0");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load state metadata first (also validates slug exists)
  useEffect(() => {
    setMeta(null);
    setMetaErr(false);
    taxAPI
      .stateDetail(stateSlug)
      .then(setMeta)
      .catch(() => setMetaErr(true));
  }, [stateSlug]);

  const title = meta ? `${meta.name} 2026 Tax Calculator — Federal + State Combined` : "State Tax Calculator";
  const seoDesc = meta
    ? `Calculate your total 2026 tax bill in ${meta.name}: federal OBBBA rules + ${meta.type === "none" ? "zero state income tax" : meta.type === "flat" ? `flat ${meta.flat_rate}% state rate` : `progressive state brackets (top ${meta.top_rate}%)`}. Free, no sign-up.`
    : "";

  useSEO({
    title,
    description: seoDesc,
    keywords: meta
      ? `${meta.name} tax calculator 2026, ${meta.name.toLowerCase()} income tax 2026, obbba ${meta.name.toLowerCase()}, ${meta.abbr} 2026 tax rates, federal state combined tax ${meta.name.toLowerCase()}`
      : undefined,
    path: `/calculators/state-income-tax/${stateSlug}`,
    jsonLd: meta
      ? {
          "@context": "https://schema.org",
          "@graph": [
            buildBreadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "State Calculators", path: "/states" },
              { name: meta.name, path: `/calculators/state-income-tax/${stateSlug}` },
            ]),
            buildCalculatorSchema({
              name: `${meta.name} 2026 Tax Calculator`,
              description: seoDesc,
              url: `/calculators/state-income-tax/${stateSlug}`,
            }),
            buildFAQSchema([
              {
                q: `Does ${meta.name} conform to OBBBA's new deductions for 2026?`,
                a: meta.notes,
              },
              {
                q: `What is the top marginal tax rate in ${meta.name} for 2026?`,
                a: meta.type === "none"
                  ? `${meta.name} has no state income tax.`
                  : meta.type === "flat"
                    ? `${meta.name} uses a flat ${meta.flat_rate}% rate on taxable income.`
                    : `${meta.name}'s top marginal rate is ${meta.top_rate}% for 2026.`,
              },
            ]),
          ],
        }
      : null,
  });

  const calculate = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.stateCombined({
        gross_income: Number(income),
        filing_status: filing,
        dependents: Number(dependents),
        itemized_deductions: 0,
        state: stateSlug,
      });
      setResult(res);
    } catch (_) {
      toast.error("Could not compute. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  if (metaErr) return <Navigate to="/states" replace />;

  if (!meta) {
    return (
      <div className="flex items-center gap-3 py-32 justify-center font-mono text-sm uppercase tracking-widest text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading...
      </div>
    );
  }

  const chartData = result
    ? [
        { label: "Federal", value: result.combined.federal_tax_2026, fill: "#0d3d2b" },
        { label: "State", value: result.combined.state_tax_2026, fill: "#c4a23a" },
        { label: "Take-home", value: result.combined.take_home, fill: "#708078" },
      ]
    : [];

  return (
    <>
      {/* Breadcrumb */}
      <div className="border-b border-foreground/20 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/states" className="hover:text-primary">States</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground">{meta.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b-2 border-foreground py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-3 block">
            {meta.abbr} · 2026 · OBBBA Edition
          </span>
          <h1
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-5 max-w-4xl"
            data-testid="state-calc-title"
          >
            {meta.name} 2026 tax calculator
          </h1>
          <p className="font-body text-lg text-foreground/80 max-w-3xl leading-relaxed">
            Combines federal OBBBA 2026 rules with {meta.name}'s 2026 state
            income tax. {meta.notes}
          </p>
        </div>
      </section>

      {/* How to use */}
      <section className="border-b border-foreground/20 py-12 md:py-16 bg-card" data-testid="state-how-to-use">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between mb-8 border-b-2 border-foreground pb-4">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold">
              How to use the {meta.name} calculator
            </h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground hidden sm:block">
              Takes about 30 seconds
            </span>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-foreground/20">
            {[
              {
                title: "Pick filing status",
                body: `Single, MFJ, Married Separate, or Head of Household. ${meta.name}'s ${meta.type === "progressive" ? "progressive brackets differ significantly by status" : meta.type === "flat" ? `flat ${meta.flat_rate}% rate still uses status for any state standard deduction` : "no state tax, but federal still uses status for brackets"}.`,
              },
              {
                title: "Enter gross income",
                body: "Total pre-tax annual income from all sources. For couples filing jointly, sum both spouses' incomes. Don't subtract 401(k) contributions — the calculator handles the federal standard deduction automatically.",
              },
              {
                title: "Add dependents",
                body: "Children under 17 unlock the $2,200 Child Tax Credit on the federal side. Most states don't provide additional credits per dependent — those that do are noted in the state's context section below.",
              },
              {
                title: "Review the combined total",
                body: `You'll see federal OBBBA 2026 tax + ${meta.name} state tax + the combined effective rate and take-home pay. Scroll down for the "Understanding your results" guide.`,
              },
            ].map((step, i) => (
              <li
                key={i}
                className="p-6 border-r border-b border-foreground/20 bg-background"
                data-testid={`state-howto-step-${i + 1}`}
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary mb-3 block">
                  Step {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-heading text-xl font-semibold mb-2 leading-tight">
                  {step.title}
                </h3>
                <p className="font-body text-sm text-foreground/80 leading-relaxed">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Calculator grid */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Input */}
            <div className="lg:col-span-5">
              <div className="bg-card border-2 border-foreground brutalist-shadow p-6 md:p-8 lg:sticky lg:top-24">
                <h2 className="font-heading text-2xl font-semibold mb-6 pb-4 border-b-2 border-foreground">
                  Your Inputs
                </h2>
                <div className="space-y-5">
                  <div>
                    <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
                    <FilingStatusSelector value={filing} onChange={setFiling} testid="state-filing" />
                  </div>
                  <div>
                    <label htmlFor="state-income" className="font-body text-sm font-semibold block mb-2">
                      Gross Annual Income (USD)
                    </label>
                    <input
                      id="state-income"
                      type="number"
                      inputMode="decimal"
                      value={income}
                      onChange={(e) => setIncome(e.target.value)}
                      className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
                      data-testid="state-input-income"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-semibold block mb-2">Dependents (under 17)</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={dependents}
                      onChange={(e) => setDependents(e.target.value)}
                      className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
                      data-testid="state-input-dependents"
                    />
                  </div>
                  <button
                    onClick={calculate}
                    disabled={loading}
                    className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-[0.2em] text-sm px-6 py-4 hover:bg-primary/90 transition disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="state-calc-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Calculate {meta.name} 2026 Tax →
                  </button>
                </div>
              </div>
            </div>

            {/* Result */}
            <div className="lg:col-span-7">
              {!result ? (
                <div className="border-2 border-dashed border-foreground/20 p-12 md:p-16 flex items-center justify-center text-center min-h-[400px]">
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground max-w-xs">
                    Enter your income and hit calculate to see your {meta.abbr} + federal 2026 total.
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Totals */}
                  <div className="grid grid-cols-2 gap-0 border border-foreground/30">
                    <div className="p-6 border-r border-foreground/30">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground block mb-2">
                        Total 2026 Tax
                      </span>
                      <p className="font-heading text-3xl md:text-4xl font-bold" data-testid="state-total-tax">
                        {formatCurrency(result.combined.total_tax)}
                      </p>
                      <p className="font-mono text-xs text-muted-foreground mt-2">
                        {formatPercent(result.combined.effective_rate)} effective
                      </p>
                    </div>
                    <div className="p-6 bg-primary text-primary-foreground">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70 block mb-2">
                        Take-home
                      </span>
                      <p className="font-heading text-3xl md:text-4xl font-bold" data-testid="state-take-home">
                        {formatCurrency(result.combined.take_home)}
                      </p>
                      <p className="font-mono text-xs opacity-70 mt-2">
                        after federal + state
                      </p>
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div className="border border-foreground/30 divide-y divide-foreground/20">
                    <div className="flex items-baseline justify-between p-5">
                      <span className="font-body text-sm">Federal income tax (OBBBA 2026)</span>
                      <span className="font-mono text-lg font-semibold">
                        {formatCurrency(result.combined.federal_tax_2026)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between p-5">
                      <span className="font-body text-sm">{meta.name} income tax</span>
                      <span className="font-mono text-lg font-semibold">
                        {formatCurrency(result.combined.state_tax_2026)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between p-5 bg-muted/30">
                      <span className="font-body text-sm font-semibold">Total tax</span>
                      <span className="font-mono text-lg font-bold">
                        {formatCurrency(result.combined.total_tax)}
                      </span>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="bg-card border border-foreground/20 p-6">
                    <h3 className="font-heading text-xl font-semibold mb-4">
                      Where your income goes
                    </h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tickLine={false} />
                        <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Legend />
                        <Bar dataKey="value" name="Amount (USD)">
                          {chartData.map((d, i) => (
                            <Cell key={i} fill={d.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Understanding your results */}
                  <ResultExplainer
                    testid="state-result-explainer"
                    takeaways={[
                      meta.type === "none"
                        ? `**${meta.name} levies no state income tax**, so your full OBBBA federal deductions (tips, overtime, senior, auto-loan, $40k SALT cap, $2,200 CTC) flow directly to your take-home pay. Residents here see the largest net benefit from OBBBA.`
                        : meta.type === "flat"
                          ? `**${meta.name} uses a flat ${meta.flat_rate}% state rate** regardless of income. Your effective state rate is roughly the same at $50k or $500k — simple planning, but no progressive relief for lower earners.`
                          : `**${meta.name}'s top marginal state rate is ${meta.top_rate}%** for 2026. Combined with the 37% top federal bracket, high earners in ${meta.name} can face combined marginal rates above ${(meta.top_rate + 37).toFixed(1)}% on the last dollar earned.`,
                      "Your **effective rate** (shown above) is total tax / gross income — much lower than your marginal rate because deductions and lower brackets stack underneath.",
                      "This estimate does **NOT include FICA** (7.65% Social Security + Medicare on wages), local/city taxes, or specialized surtaxes. Actual take-home will be lower than shown by the FICA amount at minimum.",
                      `**State conformity note:** ${meta.notes}`,
                    ]}
                    nextSteps={[
                      meta.type === "none"
                        ? `Since ${meta.name} has no state tax, focus planning on federal strategies: max 401(k) contributions, HSA, and capture all OBBBA deductions you qualify for.`
                        : `Check whether ${meta.name} conforms to specific OBBBA federal deductions (tips, overtime, bonus depreciation, QBI) — conformity varies and affects your state taxable income.`,
                      "Run the per-calculator tools to see which OBBBA deductions apply: [[Tips|no-tax-on-tips]] · [[Overtime|no-tax-on-overtime]] · [[Senior $6k|senior-deduction-2026]] · [[$40k SALT Cap|salt-deduction-2026]] · [[Child Tax Credit|child-tax-credit-2026]]. Each one feeds back into the federal side of this calculation.",
                      "Consider asking your employer about a state withholding review if your calculated state tax differs materially from what's being withheld on your paychecks.",
                      "Compare with neighboring states on the [[All 50 States|/states]] hub to see if a move could cut your combined bill — popular relocation targets include [[Texas|state:texas]], [[Florida|state:florida]], [[Tennessee|state:tennessee]], and [[Washington|state:washington]].",
                    ]}
                    glossary={[
                      COMMON_GLOSSARY.filing_status,
                      COMMON_GLOSSARY.effective_rate,
                      COMMON_GLOSSARY.marginal_rate,
                      COMMON_GLOSSARY.take_home,
                      {
                        term: "State standard deduction",
                        definition:
                          "Some states have their own standard deduction amount separate from the federal $16,100/$32,200. Others start from federal taxable income (conform) or use AGI with different adjustments.",
                      },
                      {
                        term: "Conformity",
                        definition:
                          "Whether a state automatically adopts federal tax law changes. Full conformity = state follows every OBBBA change. Partial/rolling conformity = state picks and chooses. Static conformity = state locked to federal law as of a past date (e.g., California).",
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <AdSlot size="leaderboard" testid={`state-${stateSlug}-ad`} />

      {/* Education */}
      <section className="py-16 md:py-24 border-t-2 border-foreground bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-3 block">
            The Context
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
            How OBBBA interacts with {meta.name}
          </h2>
          <p className="font-body text-base leading-relaxed text-foreground/85 mb-6">
            {meta.notes}
          </p>
          <p className="font-body text-base leading-relaxed text-foreground/85 mb-6">
            {meta.type === "none"
              ? `Because ${meta.name} levies no state income tax, every OBBBA federal deduction — the new $25k tips exclusion, $12.5k overtime deduction, $6k senior deduction, $40k SALT cap, $2,200 Child Tax Credit — flows directly to your bottom line. Residents of ${meta.name} often see the largest net-of-tax benefit from OBBBA.`
              : meta.type === "flat"
                ? `${meta.name}'s flat ${meta.flat_rate}% state rate means your marginal burden stays constant regardless of income — simpler planning, and OBBBA's new above-the-line deductions (tips, overtime, senior, auto-loan interest) reduce your federal AGI but typically don't affect your state tax base unless ${meta.name} conforms.`
                : `${meta.name}'s progressive structure (top rate ${meta.top_rate}% for 2026) stacks on top of federal. High earners often see the biggest planning opportunities here: OBBBA's raised SALT cap to $40k, QBI permanence, and bonus depreciation restoration interact with state conformity rules — which are different in every state.`}
          </p>
          <p className="font-body text-sm leading-relaxed text-muted-foreground italic">
            Estimates only. {meta.name} local add-ons, city/municipal taxes,
            credits, and nonconformity to specific federal provisions can
            change your actual bill materially. Consult a licensed tax
            professional.
          </p>
        </div>
      </section>

      {/* Related calculators */}
      <section className="py-16 border-t border-foreground/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-heading text-2xl font-semibold mb-6">Related calculators</h3>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/calculator/tax-bracket-2026"
              className="font-mono text-xs uppercase tracking-widest border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors"
              data-testid="rel-bracket"
            >
              Federal Tax Bracket Calculator →
            </Link>
            <Link
              to="/calculator/salt-deduction-2026"
              className="font-mono text-xs uppercase tracking-widest border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors"
              data-testid="rel-salt"
            >
              $40k SALT Cap Calculator →
            </Link>
            <Link
              to="/states"
              className="font-mono text-xs uppercase tracking-widest border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors"
              data-testid="rel-states"
            >
              All 50 states →
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default StateIncomeTaxPage;
